import type mapboxgl from 'mapbox-gl';
import type { ExpressionSpecification, MapLayerMouseEvent } from 'mapbox-gl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFlag } from '@/components/organisms/Vessels/utils/flagUtils';
import { useMapbox } from '@/contexts/MapboxContext';
import { useVesselStore } from '@/stores/slices/useVesselStore';
import { createMapPopup, setMapPopupContent } from '@/utils/map/mapPopup';
import { getShipType } from '@/utils/map/mapUtils';
import { fetchCdcVessel } from '../api/cdcClient';
import { useCdcStore } from '../stores/useCdcStore';
import type { CdcVessel } from '../types';

const formatWordTimestamp = (ts: string): string => {
  const d = new Date(ts);
  const day = d.getUTCDate();
  const month = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const year = d.getUTCFullYear();
  const time = d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC', hour12: false });
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${month} ${day}${suffix}, ${year}, ${time} UTC`;
};

// Vector tile source (for world view zoom 0-10)
const CDC_TILE_SOURCE_ID = 'cdc-vessels-tiles';
const CDC_TILE_LAYER_ID = 'cdc-vessels-tile-layer';
const CDC_TILE_CLUSTER_CIRCLES = 'cdc-vessels-tile-clusters';
const CDC_TILE_CLUSTER_COUNT = 'cdc-vessels-tile-cluster-count';
const CDC_SOURCE_LAYER = 'vessels';

// Live GeoJSON source (for zoomed view zoom 11+)
const CDC_LIVE_SOURCE_ID = 'cdc-vessels-live';
const CDC_LIVE_LAYER_ID = 'cdc-vessels-live-layer';
const CDC_LIVE_LABELS_ID = 'cdc-vessels-live-labels';

// Zoom threshold for switching between tiles and live data
// Tiles go up to zoom 14, set threshold higher to always use tiles (live API disabled for now)
const ZOOM_THRESHOLD = 14;

// Vector tile URL - served from CDC API
const CDC_TILE_URL = import.meta.env.VITE_CDC_API_URL || 'http://localhost:8090';

// Vessel details cache config — for on-hover full data fetch
const VESSEL_CACHE_MAX_SIZE = 500; // LRU cap - reduced to prevent memory issues

// Vessel icons that need to be loaded
export const VESSEL_ICONS = [
  'cargo',
  'tanker',
  'passenger',
  'other_type',
  'unknown_type',
  'high_speed_craft',
  'wing_in_ground',
  'reserved_for_future_use',
];

// Mapbox expression to map ship_type number to icon name
export const SHIP_TYPE_ICON_EXPRESSION: ExpressionSpecification = [
  'match',
  ['coalesce', ['get', 'ship_type'], 0],
  // Cargo: 70-79
  70,
  'cargo',
  71,
  'cargo',
  72,
  'cargo',
  73,
  'cargo',
  74,
  'cargo',
  75,
  'cargo',
  76,
  'cargo',
  77,
  'cargo',
  78,
  'cargo',
  79,
  'cargo',
  // Tanker: 80-89
  80,
  'tanker',
  81,
  'tanker',
  82,
  'tanker',
  83,
  'tanker',
  84,
  'tanker',
  85,
  'tanker',
  86,
  'tanker',
  87,
  'tanker',
  88,
  'tanker',
  89,
  'tanker',
  // Passenger: 60-69
  60,
  'passenger',
  61,
  'passenger',
  62,
  'passenger',
  63,
  'passenger',
  64,
  'passenger',
  65,
  'passenger',
  66,
  'passenger',
  67,
  'passenger',
  68,
  'passenger',
  69,
  'passenger',
  // High speed craft: 40-49
  40,
  'high_speed_craft',
  41,
  'high_speed_craft',
  42,
  'high_speed_craft',
  43,
  'high_speed_craft',
  44,
  'high_speed_craft',
  45,
  'high_speed_craft',
  46,
  'high_speed_craft',
  47,
  'high_speed_craft',
  48,
  'high_speed_craft',
  49,
  'high_speed_craft',
  // Wing in ground: 20-29
  20,
  'wing_in_ground',
  21,
  'wing_in_ground',
  22,
  'wing_in_ground',
  23,
  'wing_in_ground',
  24,
  'wing_in_ground',
  25,
  'wing_in_ground',
  26,
  'wing_in_ground',
  27,
  'wing_in_ground',
  28,
  'wing_in_ground',
  29,
  'wing_in_ground',
  // Sailing/Pleasure (36-37)
  36,
  'passenger',
  37,
  'passenger',
  // Fishing, Tug, etc. -> other_type
  30,
  'other_type',
  31,
  'other_type',
  32,
  'other_type',
  33,
  'other_type',
  34,
  'other_type',
  35,
  'other_type',
  50,
  'other_type',
  51,
  'other_type',
  52,
  'other_type',
  53,
  'other_type',
  54,
  'other_type',
  55,
  'other_type',
  56,
  'other_type',
  57,
  'other_type',
  58,
  'other_type',
  59,
  'other_type',
  90,
  'other_type',
  91,
  'other_type',
  92,
  'other_type',
  93,
  'other_type',
  94,
  'other_type',
  95,
  'other_type',
  96,
  'other_type',
  97,
  'other_type',
  98,
  'other_type',
  99,
  'other_type',
  // Default fallback
  'unknown_type',
];

/**
 * CdcVesselLayer - Renders CDC vessels with VECTOR TILES
 *
 * Vector tiles approach for INSTANT loading:
 * 1. Map requests only the tiles visible in viewport
 * 2. Server-side clustering via tippecanoe
 * 3. ~2 second load time for world view (vs 2+ minutes with GeoJSON)
 *
 * Trade-off: Requires tile server, but INSTANT loading at any zoom
 */
export const CdcVesselLayer: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const navigate = useNavigate();
  const [layersAdded, setLayersAdded] = useState(false);
  const [iconsLoaded, setIconsLoaded] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false); // Track if we're past zoom threshold
  const selectedMmsi = useCdcStore((state) => state.selectedMmsi);
  const setSelectedMmsi = useCdcStore((state) => state.setSelectedMmsi);
  const setSelectedVesselCoords = useCdcStore((state) => state.setSelectedVesselCoords);
  const relatedVesselMmsis = useCdcStore((state) => state.relatedVesselMmsis);
  const activeRelatedFilters = useCdcStore((state) => state.activeRelatedFilters);
  const hasActiveRelatedFilters = Object.values(activeRelatedFilters).some(Boolean);
  const animatingMmsi = useCdcStore((state) => state.animatingMmsi);
  const isAreaTrafficActive = useCdcStore((state) => state.isAreaTrafficActive);

  // Vessel store for syncing to sidebar panels
  const setVesselsInStore = useVesselStore((state) => state.setVessels);

  // Ref for live data fetch abort controller
  const liveDataAbortRef = useRef<AbortController | null>(null);

  // Popup ref for hover tooltip (reused across hovers to avoid DOM churn)
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Track previous vessel sync to skip redundant store updates
  const prevSyncKeyRef = useRef<string>('');

  // On-hover full vessel data cache (slim data has no names, so fetch on hover)
  const vesselCacheRef = useRef<Map<string, CdcVessel>>(new Map());
  const inflightFetchRef = useRef<Map<string, Promise<CdcVessel | null>>>(new Map());
  const hoveredMmsiRef = useRef<string | null>(null);

  // Helper: Fetch full vessel details with cache and in-flight deduplication
  // Limits concurrent fetches to prevent memory issues from rapid hovering
  const getFullVesselDetails = useCallback(async (mmsi: string): Promise<CdcVessel | null> => {
    // Check cache first
    const cached = vesselCacheRef.current.get(mmsi);
    if (cached) return cached;

    // Check if already fetching
    const inflight = inflightFetchRef.current.get(mmsi);
    if (inflight) return inflight;

    // Limit concurrent fetches to prevent memory issues
    if (inflightFetchRef.current.size >= 3) {
      return null; // Skip if too many in-flight
    }

    // Start fetch
    const fetchPromise = (async () => {
      try {
        const res = await fetchCdcVessel(mmsi);
        if (res?.vessel) {
          // Add to cache with LRU eviction
          const cache = vesselCacheRef.current;
          cache.set(mmsi, res.vessel);
          if (cache.size > VESSEL_CACHE_MAX_SIZE) {
            // Delete oldest entry (Map preserves insertion order)
            const oldestKey = cache.keys().next().value;
            if (oldestKey) cache.delete(oldestKey);
          }
          return res.vessel;
        }
        return null;
      } catch {
        return null;
      } finally {
        inflightFetchRef.current.delete(mmsi);
      }
    })();

    inflightFetchRef.current.set(mmsi, fetchPromise);
    return fetchPromise;
  }, []);

  // Fetch live vessels from bbox/slim API and update GeoJSON source
  const fetchLiveVessels = useCallback(async () => {
    if (!map.current || !layersAdded) return;

    const mapInstance = map.current;
    const bounds = mapInstance.getBounds();
    if (!bounds) return;

    // Cancel previous request
    if (liveDataAbortRef.current) {
      liveDataAbortRef.current.abort();
    }
    liveDataAbortRef.current = new AbortController();

    // Build query params - API expects minLat, maxLat, minLon, maxLon
    const params = new URLSearchParams({
      minLat: bounds.getSouth().toString(),
      maxLat: bounds.getNorth().toString(),
      minLon: bounds.getWest().toString(),
      maxLon: bounds.getEast().toString(),
      limit: '10000',
    });

    try {
      const response = await fetch(`${CDC_TILE_URL}/vessels/bbox/slim?${params}`, {
        signal: liveDataAbortRef.current.signal,
      });

      if (!response.ok) return;

      // API returns GeoJSON FeatureCollection directly
      const geojson: GeoJSON.FeatureCollection = await response.json();

      // Update the live source
      const source = mapInstance.getSource(CDC_LIVE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(geojson);
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('[CdcVesselLayer] Failed to fetch live vessels:', err);
      }
    }
  }, [map, layersAdded]);

  // Sync visible vessels to the global vessel store for sidebar panels
  // Uses queryRenderedFeatures to get vessels currently visible in viewport
  const syncVisibleVessels = useCallback(() => {
    if (!map.current || !layersAdded) return;

    const mapInstance = map.current;
    const zoom = mapInstance.getZoom();

    // Query from the active layer based on zoom
    const activeLayerId = zoom >= ZOOM_THRESHOLD ? CDC_LIVE_LAYER_ID : CDC_TILE_LAYER_ID;
    if (!mapInstance.getLayer(activeLayerId)) return;

    // Get visible individual vessels
    const features = mapInstance.queryRenderedFeatures(undefined, {
      layers: [activeLayerId],
    });

    if (!features || features.length === 0) return;

    // Filter and deduplicate by MMSI
    // LIMIT to 5000 to prevent memory issues - sidebar can't display all anyway
    const seenMmsis = new Set<string>();
    const individualVessels: mapboxgl.MapboxGeoJSONFeature[] = [];
    const SYNC_LIMIT = 5000;

    for (const f of features) {
      if (individualVessels.length >= SYNC_LIMIT) break;
      if (f.properties?.clustered === true) continue; // Skip clusters
      const mmsi = f.properties?.mmsi;
      if (!mmsi || mmsi === '0' || mmsi === 0) continue; // Skip invalid
      const mmsiStr = String(mmsi);
      if (seenMmsis.has(mmsiStr)) continue; // Skip duplicate
      seenMmsis.add(mmsiStr);
      individualVessels.push(f);
    }

    if (individualVessels.length === 0) return;

    const firstMmsi = individualVessels[0]?.properties?.mmsi || '';
    const syncKey = `${individualVessels.length}:${firstMmsi}:${zoom >= ZOOM_THRESHOLD ? 'live' : 'tiles'}`;

    if (syncKey === prevSyncKeyRef.current) return;
    prevSyncKeyRef.current = syncKey;

    const vesselPositions = individualVessels.map((f) => {
      const props = f.properties || {};
      const geometry = f.geometry as GeoJSON.Point;
      const [lon, lat] = geometry.coordinates as [number, number];

      return {
        mmsi: Number(props.mmsi) || props.mmsi,
        vessel_name: props.name || '',
        imo: props.imo,
        vessel_type: props.ship_type,
        flag_code: props.flag_mid,
        flag_country: props.flag_country,
        destination: props.destination,
        speed_over_ground: props.sog,
        latitude: lat,
        longitude: lon,
        lat,
        lon,
        timestamp_utc: props.last_ts || new Date().toISOString(),
      };
    });

    setVesselsInStore(vesselPositions);
  }, [map, layersAdded, setVesselsInStore]);

  // Sync visible vessels when map moves, tiles load, or zoom mode changes
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded) return;

    const mapInstance = map.current;

    // Initial sync (delayed to allow data to load)
    const initialTimeout = setTimeout(syncVisibleVessels, 500);

    // Sync on map move
    const handleMoveEnd = () => syncVisibleVessels();
    mapInstance.on('moveend', handleMoveEnd);

    // Sync when new data is loaded
    const handleSourceData = (e: mapboxgl.MapSourceDataEvent) => {
      if ((e.sourceId === CDC_TILE_SOURCE_ID || e.sourceId === CDC_LIVE_SOURCE_ID) && e.isSourceLoaded) {
        syncVisibleVessels();
      }
    };
    mapInstance.on('sourcedata', handleSourceData);

    return () => {
      clearTimeout(initialTimeout);
      mapInstance.off('moveend', handleMoveEnd);
      mapInstance.off('sourcedata', handleSourceData);
    };
  }, [isMapReady, map, layersAdded, syncVisibleVessels]);

  // Load vessel icons
  useEffect(() => {
    if (!isMapReady || !map.current || iconsLoaded) return;

    const mapInstance = map.current;
    let loadedCount = 0;

    const loadIcon = (iconId: string) => {
      if (mapInstance.hasImage(iconId)) {
        loadedCount++;
        if (loadedCount === VESSEL_ICONS.length) setIconsLoaded(true);
        return;
      }

      mapInstance.loadImage(`/vessels/${iconId}.png`, (error, image) => {
        if (!error && image && !mapInstance.hasImage(iconId)) {
          try {
            mapInstance.addImage(iconId, image);
          } catch {
            // Image may have been added by another call
          }
        }
        loadedCount++;
        if (loadedCount === VESSEL_ICONS.length) setIconsLoaded(true);
      });
    };

    VESSEL_ICONS.forEach(loadIcon);
  }, [isMapReady, map, iconsLoaded]);

  // Add HYBRID layers - vector tiles for world view, live GeoJSON for zoomed view
  useEffect(() => {
    if (!isMapReady || !map.current || !iconsLoaded) return;

    const mapInstance = map.current;

    if (!layersAdded) {
      // === VECTOR TILE SOURCE (zoom 0-10) ===
      if (!mapInstance.getSource(CDC_TILE_SOURCE_ID)) {
        mapInstance.addSource(CDC_TILE_SOURCE_ID, {
          type: 'vector',
          tiles: [`${CDC_TILE_URL}/tiles/vessels/{z}/{x}/{y}.mvt`],
          minzoom: 0,
          maxzoom: 14,
        });
      }

      // Cluster circles layer - clustered: true
      if (!mapInstance.getLayer(CDC_TILE_CLUSTER_CIRCLES)) {
        mapInstance.addLayer({
          id: CDC_TILE_CLUSTER_CIRCLES,
          type: 'circle',
          source: CDC_TILE_SOURCE_ID,
          'source-layer': CDC_SOURCE_LAYER,
          maxzoom: ZOOM_THRESHOLD,
          filter: ['==', ['get', 'clustered'], true], // Clusters have clustered: true
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#6b6e64', // < 50 vessels - military gray
              50,
              '#3d6b2e', // 50-250 vessels - darker green
              250,
              '#2f6a5e', // 250-500 vessels - teal green
              500,
              '#2d5a8a', // 500-1000 vessels - dark blue
              1000,
              '#3b82f6', // 1000-5000 vessels - panel blue
              5000,
              '#3b82f6', // 5000+ vessels - panel blue
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              15, // base size
              100,
              20,
              1000,
              30,
              10000,
              40,
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          },
        });
      }

      // Cluster count labels
      if (!mapInstance.getLayer(CDC_TILE_CLUSTER_COUNT)) {
        mapInstance.addLayer({
          id: CDC_TILE_CLUSTER_COUNT,
          type: 'symbol',
          source: CDC_TILE_SOURCE_ID,
          'source-layer': CDC_SOURCE_LAYER,
          maxzoom: ZOOM_THRESHOLD,
          filter: ['==', ['get', 'clustered'], true],
          layout: {
            'text-field': [
              'case',
              ['>=', ['get', 'point_count'], 1000],
              ['concat', ['to-string', ['/', ['round', ['/', ['get', 'point_count'], 100]], 10]], 'k'],
              ['to-string', ['get', 'point_count']],
            ],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#e2e8f0', // Light gray - readable on dark cluster colors
          },
        });
      }

      // Move ports layer below clusters so clusters appear on top
      // Ports load asynchronously, so retry a few times to ensure correct z-order
      const movePortsBelowClusters = () => {
        if (mapInstance.getLayer('Ports Layer') && mapInstance.getLayer(CDC_TILE_CLUSTER_CIRCLES)) {
          try {
            mapInstance.moveLayer('Ports Layer', CDC_TILE_CLUSTER_CIRCLES);
          } catch {
            // Layer might already be in correct position
          }
        }
      };
      movePortsBelowClusters();
      mapInstance.once('idle', movePortsBelowClusters);
      // Retry after delays to catch async-loaded ports
      setTimeout(movePortsBelowClusters, 1000);
      setTimeout(movePortsBelowClusters, 3000);

      // Tile layer - individual vessel icons (clustered: false)
      if (!mapInstance.getLayer(CDC_TILE_LAYER_ID)) {
        mapInstance.addLayer({
          id: CDC_TILE_LAYER_ID,
          type: 'symbol',
          source: CDC_TILE_SOURCE_ID,
          'source-layer': CDC_SOURCE_LAYER,
          maxzoom: ZOOM_THRESHOLD, // Hide at zoom 11+ (when live data takes over)
          filter: ['==', ['get', 'clustered'], false], // Individual vessels have clustered: false
          layout: {
            'icon-image': SHIP_TYPE_ICON_EXPRESSION,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.04, 5, 0.06, 10, 0.08],
            'icon-rotate': ['coalesce', ['get', 'true_heading'], ['get', 'cog'], 0],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            visibility: 'visible',
          },
        });
      }

      // === LIVE GEOJSON SOURCE (zoom 11+) ===
      if (!mapInstance.getSource(CDC_LIVE_SOURCE_ID)) {
        mapInstance.addSource(CDC_LIVE_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }

      // Live layer - vessel icons from API (visible at zoom >= 11)
      if (!mapInstance.getLayer(CDC_LIVE_LAYER_ID)) {
        mapInstance.addLayer({
          id: CDC_LIVE_LAYER_ID,
          type: 'symbol',
          source: CDC_LIVE_SOURCE_ID,
          minzoom: ZOOM_THRESHOLD, // Show at zoom 11+
          layout: {
            'icon-image': SHIP_TYPE_ICON_EXPRESSION,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.08, 14, 0.1],
            'icon-rotate': ['coalesce', ['get', 'true_heading'], ['get', 'cog'], 0],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            visibility: 'visible',
          },
        });
      }

      // Live vessel name labels (zoom 11+)
      if (!mapInstance.getLayer(CDC_LIVE_LABELS_ID)) {
        mapInstance.addLayer({
          id: CDC_LIVE_LABELS_ID,
          type: 'symbol',
          source: CDC_LIVE_SOURCE_ID,
          minzoom: ZOOM_THRESHOLD,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 11,
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-optional': true,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1,
          },
        });
      }

      setLayersAdded(true);
    }
  }, [isMapReady, map, layersAdded, iconsLoaded]);

  // Hide all CDC vessel layers when Area Traffic replay is active
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded) return;

    const mapInstance = map.current;
    const visibility = isAreaTrafficActive ? 'none' : 'visible';

    // Hide/show all vessel layers
    const layerIds = [
      CDC_TILE_LAYER_ID,
      CDC_TILE_CLUSTER_CIRCLES,
      CDC_TILE_CLUSTER_COUNT,
      CDC_LIVE_LAYER_ID,
      CDC_LIVE_LABELS_ID,
    ];

    for (const layerId of layerIds) {
      if (mapInstance.getLayer(layerId)) {
        mapInstance.setLayoutProperty(layerId, 'visibility', visibility);
      }
    }

    // Also dismiss any hover popup when Area Traffic starts
    if (isAreaTrafficActive && popupRef.current) {
      popupRef.current.remove();
      hoveredMmsiRef.current = null;
    }
  }, [isMapReady, map, layersAdded, isAreaTrafficActive]);

  // Track zoom level and fetch live data when zoomed in
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded) return;

    const mapInstance = map.current;

    const handleZoom = () => {
      const zoom = mapInstance.getZoom();
      const nowZoomedIn = zoom >= ZOOM_THRESHOLD;

      if (nowZoomedIn !== isZoomedIn) {
        setIsZoomedIn(nowZoomedIn);
      }

      // Fetch live data when zoomed in
      if (nowZoomedIn) {
        fetchLiveVessels();
      }
    };

    // Check initial zoom
    handleZoom();

    // Listen for zoom/move changes
    mapInstance.on('moveend', handleZoom);

    return () => {
      mapInstance.off('moveend', handleZoom);
    };
  }, [isMapReady, map, layersAdded, isZoomedIn, fetchLiveVessels]);

  // Handle vessel click and hover (works for both tile and live layers)
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded) return;

    const mapInstance = map.current;

    // Handle vessel click — select, open panel, AND fly to vessel
    const handleVesselClick = (e: MapLayerMouseEvent) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const mmsi = feature.properties?.mmsi as string;
        const geometry = feature.geometry as GeoJSON.Point;
        const coords = geometry?.coordinates as [number, number];

        if (mmsi) {
          setSelectedMmsi(mmsi);
          setSelectedVesselCoords({ lat: coords[1], lon: coords[0] });

          // Open vessel detail panel
          navigate(`/vessels/${mmsi}`);

          // Fly to vessel location (zoom to at least 11 to see live data)
          if (coords) {
            mapInstance.flyTo({
              center: coords,
              zoom: Math.max(mapInstance.getZoom(), ZOOM_THRESHOLD),
              duration: 1000,
            });
          }
        }
      }
    };

    const handleMouseEnter = (e: MapLayerMouseEvent) => {
      mapInstance.getCanvas().style.cursor = 'pointer';

      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const props = feature.properties || {};
        const mmsi = props.mmsi as string;
        const shipType = props.ship_type as number | undefined;
        const cog = props.cog as number | undefined;
        const geometry = feature.geometry as GeoJSON.Point;
        const coordinates = geometry?.coordinates as [number, number];

        if (!coordinates || !mmsi) return;

        // Track which vessel we're hovering
        hoveredMmsiRef.current = mmsi;

        // Helper to build popup props from vessel data
        const buildPopupProps = (vessel?: CdcVessel | null) => {
          const name = vessel?.name;
          const imo = vessel?.imo;
          const callsign = vessel?.callsign;
          const destination = vessel?.destination;
          const sog = vessel?.sog;
          const flagMid = vessel?.flag_mid;
          const lastTs = vessel?.last_ts;
          const flag = getFlag(flagMid, mmsi);

          return {
            badge: { label: getShipType(shipType), variant: 'info' as const },
            title: name || (vessel ? 'Unknown vessel' : `MMSI ${mmsi}`),
            subtitle: lastTs
              ? `Updated ${formatWordTimestamp(lastTs)}`
              : vessel
                ? undefined
                : 'Loading details...',
            icon: flag?.icon_png
              ? { src: flag.icon_png, alt: flag.flag_name || 'Vessel flag', shape: 'square' as const }
              : undefined,
            sections: [
              {
                id: 'identity',
                columns: 2,
                fields: [
                  { label: 'MMSI', value: mmsi, status: 'default' as const },
                  {
                    label: 'IMO',
                    value: imo || (vessel ? 'Unknown' : '...'),
                    status: imo ? ('default' as const) : ('warning' as const),
                  },
                  {
                    label: 'Call Sign',
                    value: callsign || (vessel ? 'Unknown' : '...'),
                    status: callsign ? ('default' as const) : ('warning' as const),
                  },
                  {
                    label: 'Destination',
                    value: destination || (vessel ? 'Unknown' : '...'),
                    status: destination ? ('default' as const) : ('warning' as const),
                  },
                ],
              },
              {
                id: 'telemetry',
                columns: 2,
                fields: [
                  {
                    label: 'Coordinates',
                    value: `${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}`,
                    valueClassName: 'font-mono',
                  },
                  { label: 'Speed', value: sog != null ? `${sog.toFixed(1)} kn` : vessel ? 'Unknown' : '...' },
                  { label: 'Course', value: cog != null ? `${cog.toFixed(0)}°` : 'Unknown' },
                  { label: 'Flag', value: flag?.flag_name || (vessel ? 'Unknown' : '...') },
                ],
              },
            ],
          };
        };

        // Check if we have cached full data
        const cachedVessel = vesselCacheRef.current.get(mmsi);

        // Show tooltip immediately (with cached data or basic slim data)
        const popupProps = buildPopupProps(cachedVessel);
        if (!popupRef.current) {
          popupRef.current = createMapPopup(coordinates, popupProps);
        } else {
          setMapPopupContent(popupRef.current, coordinates, popupProps);
        }
        popupRef.current.addTo(mapInstance);

        // If not cached, fetch full details in background
        if (!cachedVessel) {
          void (async () => {
            const fullVessel = await getFullVesselDetails(mmsi);
            // Only update if still hovering the same vessel
            if (hoveredMmsiRef.current === mmsi && fullVessel && popupRef.current) {
              const updatedProps = buildPopupProps(fullVessel);
              setMapPopupContent(popupRef.current, coordinates, updatedProps);
            }
          })();
        }
      }
    };

    const handleMouseLeave = () => {
      mapInstance.getCanvas().style.cursor = '';
      hoveredMmsiRef.current = null; // Clear hover state to prevent stale updates
      // Clear in-flight fetches to prevent memory buildup from rapid hovering
      inflightFetchRef.current.clear();
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };

    // Cluster click - zoom in to expand
    const handleClusterClick = (e: MapLayerMouseEvent) => {
      const features = mapInstance.queryRenderedFeatures(e.point, {
        layers: [CDC_TILE_CLUSTER_CIRCLES],
      });
      if (!features.length) return;

      const geometry = features[0].geometry as GeoJSON.Point;
      const currentZoom = mapInstance.getZoom();
      const pointCount = features[0].properties?.point_count || 0;

      // Zoom in more for larger clusters
      const zoomIncrement = pointCount > 10000 ? 3 : pointCount > 1000 ? 2.5 : 2;

      mapInstance.easeTo({
        center: geometry.coordinates as [number, number],
        zoom: Math.min(currentZoom + zoomIncrement, 14),
        duration: 500,
      });
    };

    const handleClusterMouseEnter = () => {
      mapInstance.getCanvas().style.cursor = 'pointer';
    };

    const handleClusterMouseLeave = () => {
      mapInstance.getCanvas().style.cursor = '';
    };

    // Tile layer events (zoom < 11)
    mapInstance.on('click', CDC_TILE_LAYER_ID, handleVesselClick);
    mapInstance.on('mouseenter', CDC_TILE_LAYER_ID, handleMouseEnter);
    mapInstance.on('mouseleave', CDC_TILE_LAYER_ID, handleMouseLeave);

    // Cluster events
    mapInstance.on('click', CDC_TILE_CLUSTER_CIRCLES, handleClusterClick);
    mapInstance.on('mouseenter', CDC_TILE_CLUSTER_CIRCLES, handleClusterMouseEnter);
    mapInstance.on('mouseleave', CDC_TILE_CLUSTER_CIRCLES, handleClusterMouseLeave);

    // Live layer events (zoom >= 11)
    mapInstance.on('click', CDC_LIVE_LAYER_ID, handleVesselClick);
    mapInstance.on('mouseenter', CDC_LIVE_LAYER_ID, handleMouseEnter);
    mapInstance.on('mouseleave', CDC_LIVE_LAYER_ID, handleMouseLeave);

    return () => {
      mapInstance.off('click', CDC_TILE_LAYER_ID, handleVesselClick);
      mapInstance.off('mouseenter', CDC_TILE_LAYER_ID, handleMouseEnter);
      mapInstance.off('mouseleave', CDC_TILE_LAYER_ID, handleMouseLeave);
      mapInstance.off('click', CDC_TILE_CLUSTER_CIRCLES, handleClusterClick);
      mapInstance.off('mouseenter', CDC_TILE_CLUSTER_CIRCLES, handleClusterMouseEnter);
      mapInstance.off('mouseleave', CDC_TILE_CLUSTER_CIRCLES, handleClusterMouseLeave);
      mapInstance.off('click', CDC_LIVE_LAYER_ID, handleVesselClick);
      mapInstance.off('mouseenter', CDC_LIVE_LAYER_ID, handleMouseEnter);
      mapInstance.off('mouseleave', CDC_LIVE_LAYER_ID, handleMouseLeave);
    };
  }, [isMapReady, map, layersAdded, setSelectedMmsi, setSelectedVesselCoords, navigate, getFullVesselDetails]);

  // Dim all vessels and clusters when related filters are active
  // Related vessels are rendered at full opacity in the separate CdcConnectionLines layer
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded) return;

    const mapInstance = map.current;
    const dimOpacity = hasActiveRelatedFilters ? 0.1 : 1;

    // Dim all individual vessel markers (related ones show through CdcConnectionLines layer)
    for (const layerId of [CDC_TILE_LAYER_ID, CDC_LIVE_LAYER_ID]) {
      if (mapInstance.getLayer(layerId)) {
        mapInstance.setPaintProperty(layerId, 'icon-opacity', dimOpacity);
      }
    }

    // Dim clusters so related markers show through at low zoom
    if (mapInstance.getLayer(CDC_TILE_CLUSTER_CIRCLES)) {
      mapInstance.setPaintProperty(CDC_TILE_CLUSTER_CIRCLES, 'circle-opacity', dimOpacity);
      mapInstance.setPaintProperty(CDC_TILE_CLUSTER_CIRCLES, 'circle-stroke-opacity', dimOpacity);
    }
    if (mapInstance.getLayer(CDC_TILE_CLUSTER_COUNT)) {
      mapInstance.setPaintProperty(CDC_TILE_CLUSTER_COUNT, 'text-opacity', dimOpacity);
    }
  }, [isMapReady, map, layersAdded, hasActiveRelatedFilters]);

  // Sync selected vessel position from visible features (query both layers)
  // Falls back to API fetch if vessel is in a cluster (not individually rendered)
  useEffect(() => {
    if (!selectedMmsi || !map.current || !layersAdded) return;

    const mapInstance = map.current;

    // Try live layer first (more accurate), then tile layer
    const layersToQuery = [CDC_LIVE_LAYER_ID, CDC_TILE_LAYER_ID].filter((id) => mapInstance.getLayer(id));

    for (const layerId of layersToQuery) {
      const features = mapInstance.queryRenderedFeatures(undefined, {
        layers: [layerId],
        filter: ['==', ['get', 'mmsi'], selectedMmsi],
      });

      if (features.length > 0) {
        const feature = features[0];
        if (feature.geometry.type === 'Point') {
          const coords = feature.geometry.coordinates as [number, number];
          setSelectedVesselCoords({ lat: coords[1], lon: coords[0] });
          return;
        }
      }
    }

    // Vessel not found in rendered features (likely in a cluster at low zoom)
    // Fetch coords from CDC API as fallback for pulsating animation
    const fetchVesselCoords = async () => {
      try {
        const res = await fetchCdcVessel(selectedMmsi);
        if (res?.vessel?.lat && res?.vessel?.lon) {
          setSelectedVesselCoords({ lat: res.vessel.lat, lon: res.vessel.lon });
        }
      } catch {
        // Ignore - vessel position unknown
      }
    };

    fetchVesselCoords();
  }, [selectedMmsi, setSelectedVesselCoords, map, layersAdded]);

  // Cleanup layers on unmount
  useEffect(() => {
    return () => {
      if (!map.current) return;
      const mapInstance = map.current;

      try {
        // Remove live layers
        if (mapInstance.getLayer(CDC_LIVE_LABELS_ID)) mapInstance.removeLayer(CDC_LIVE_LABELS_ID);
        if (mapInstance.getLayer(CDC_LIVE_LAYER_ID)) mapInstance.removeLayer(CDC_LIVE_LAYER_ID);
        if (mapInstance.getSource(CDC_LIVE_SOURCE_ID)) mapInstance.removeSource(CDC_LIVE_SOURCE_ID);

        // Remove tile layers
        if (mapInstance.getLayer(CDC_TILE_LAYER_ID)) mapInstance.removeLayer(CDC_TILE_LAYER_ID);
        if (mapInstance.getLayer(CDC_TILE_CLUSTER_COUNT)) mapInstance.removeLayer(CDC_TILE_CLUSTER_COUNT);
        if (mapInstance.getLayer(CDC_TILE_CLUSTER_CIRCLES)) mapInstance.removeLayer(CDC_TILE_CLUSTER_CIRCLES);
        if (mapInstance.getSource(CDC_TILE_SOURCE_ID)) mapInstance.removeSource(CDC_TILE_SOURCE_ID);
      } catch {
        // Map may already be destroyed
      }
    };
  }, [map]);

  // Vector tiles load on demand - no loading state needed
  // Tiles are requested automatically by Mapbox when viewport changes
  return null;
};
